import {
  EcrPrerequisitesRequestDtoEcrWorkflowModeEnum,
  OrgDetailResponseDtoQarVerifiedEnum
} from '@/api/origin-workflow-svc'
import { EcrWorkflow } from '@/models/ecr-vlei-workflow'
import { formatISO, parse, set } from 'date-fns'
import { format } from 'date-fns-tz'
import { isEmpty } from 'lodash'

export const objToQuery = (obj: any) => {
  return new URLSearchParams(obj).toString()
}

export const queryToObj = (queryString: string) => {
  return new URLSearchParams(queryString)
}

export const bytesToSize = (bytes: number): string => {
  const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes === 0) return '0 Byte'
  const i: number = Math.floor(Math.log(bytes) / Math.log(1024))
  return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i]
}

export const getJWTPayload = (token: string = '') => {
  const encodedPayload = token.split('.')[1] || ''
  const payload = Buffer.from(encodedPayload, 'base64').toString('ascii')
  return !isEmpty(payload) ? JSON.parse(payload) : null
}

export const UTCtoLocal = (utcDateString: string, frmt: string) => {
  if (!utcDateString) return ''
  const parsedTime = new Date(utcDateString)
  return format(parsedTime, frmt)
}

/**
 * Checks if a given object contains a specific key.
 *
 * @template T - The type of the object being checked.
 * @param {T} obj - The object to check if it has key. It can be of any type.
 * @param {keyof any} key - The key to check for in the object.
 * @returns {boolean} - Returns `true` if the object is neither `null` nor `undefined`,
 *                      is an actual object (not an array), and contains the specified key.
 *                      Otherwise, returns `false`.
 *
 */
export const hasKey = <T>(obj: T, key: keyof any): boolean => {
  if (obj === null || obj === undefined) {
    return false
  }

  if (typeof obj !== 'object' || Array.isArray(obj)) {
    return false
  }

  return key in obj
}

export const ceremonyTypeFromURL = () => {
  const urlObject = new URL(window.location.href)
  const pathSegments = urlObject.pathname.split('/')
  return pathSegments[1].toUpperCase()
}

export const isAllUpperCase = (str: string): boolean => {
  return /^[A-Z0-9]*$/.test(str)
}

export const delay = (timeout: number = 1000): Promise<void> => {
  return new Promise<void>((resolve) => setTimeout(resolve, timeout))
}

export const formatUrlPath = (base: string, path: string): string | null => {
  if (!base) return null
  const cleanedBase = base.replace(/\/+$/, '')
  const cleanedPath = path.replace(/^\/+/, '')
  return `${cleanedBase}/${cleanedPath}`
}

export const isOrganizationVerified = (
  qarVerified: OrgDetailResponseDtoQarVerifiedEnum | null | undefined
) => {
  return (
    qarVerified === OrgDetailResponseDtoQarVerifiedEnum.OrgVerified ||
    qarVerified === OrgDetailResponseDtoQarVerifiedEnum.RepresentativesVerified
  )
}

export const isRepresentativesApproved = (
  qarVerified: OrgDetailResponseDtoQarVerifiedEnum | null | undefined
) => {
  return (
    qarVerified === OrgDetailResponseDtoQarVerifiedEnum.RepresentativesVerified
  )
}

export const isSelfIssued = (workflow: EcrWorkflow | null) => {
  // `WorkflowInstanceDto.args` is generated as optional. For ECR-prereq
  // workflows the field is populated by the backend, but we treat absence
  // as "not self-issued" rather than crashing on `.workflowMode` access.
  return (
    workflow?.rawData.args?.workflowMode ===
    EcrPrerequisitesRequestDtoEcrWorkflowModeEnum.SelfIssued
  )
}

export const formatToISO8601 = (date: Date, time: string) => {
  time = time.length === 8 ? time : `${time}:00`
  const parsedTime = parse(time, 'HH:mm:ss', new Date())

  const mergedDateTime = set(date, {
    hours: parsedTime.getHours(),
    minutes: parsedTime.getMinutes(),
    seconds: parsedTime.getSeconds()
  })

  return formatISO(mergedDateTime)
}

// URLs matching these patterns will skip:
// 1. Waiting for SignifyClient initialization (passcode prompt)
// 2. Signed headers
//
// cd-0010: All path patterns are anchored with ^ and $ (or ^….*$ for prefix
// matches) to prevent a substring match from smuggling a public-endpoint
// prefix into an otherwise-private path.  The four Jitsi patterns already
// carry ^ anchors and match full URLs; call sites extract the pathname first,
// so those patterns currently never fire in production — that pre-existing
// mismatch is outside cd-0010 scope and is left unchanged here to avoid
// unintended behaviour changes.
//
// cd-0016: Exact-anchored path patterns now use \/?$ instead of a bare $ so
// that a single optional trailing slash is tolerated.  The prefix patterns
// (ending in .*$) are unchanged — .* already matches a trailing slash.  The
// four Jitsi full-URL patterns are also unchanged (cd-0017 scope).
const BLACK_LIST_URL_PATTERNS = [
  /^https:\/\/jitsi\.origin\.dev\.provenant\.net\/.*/,
  /^https:\/\/jitsi\.dev\.provenant\.net\/.*/,
  /^wss:\/\/jitsi\.dev\.provenant\.net\/.*/,
  /^wss:\/\/jitsi\.origin\.dev\.provenant\.net\/.*/,
  // Jitsi transport endpoints, matched by PATHNAME. The four full-URL patterns
  // above can never fire in production because both call sites (apis.ts
  // makeCustomFetch, signify.ts attachInterceptor) pass `new URL(url).pathname`
  // — e.g. `/xmpp-websocket` — which does not match a `^https://jitsi…` regex.
  // Without these pathname entries, Jitsi requests receive Signify signed
  // headers and the Jitsi server's CORS preflight rejects `signature-input`,
  // so the ceremony websocket never connects. Anchored to the exact transport
  // paths (optional trailing slash) so no unrelated app route is caught.
  /^\/xmpp-websocket\/?$/, // Jitsi XMPP-over-WebSocket transport
  /^\/http-bind\/?$/, // Jitsi BOSH transport
  /^\/colibri-ws\/.*$/, // Jitsi Videobridge colibri websocket
  /^\/v1\/l10n\/localized-text\/ui\/?$/, // Localization endpoint (public API)
  /^\/v1\/user-mgmt\/email\/origin-aid\/.*$/, // Email origin-aid lookup endpoint (public API)
  /^\/v1\/user-mgmt\/auth\/onboard\/?$/,
  /^\/v1\/user-mgmt\/auth\/resend-confirmation\/?$/,
  /^\/v1\/user-mgmt\/auth\/confirm\/.*$/,
  /^\/v1\/user-mgmt\/auth\/wallet\/?$/,
  /^\/v1\/user-mgmt\/auth\/password\/?$/,
  /^\/v1\/user-mgmt\/user\/origin-aid\/.*$/,
  /^\/v1\/user-mgmt\/auth\/join\/?$/,
  /^\/v1\/user-mgmt\/auth\/profile\/?$/,
  /^\/v1\/workflow\/util\/acceptance\/.*$/
  // Add your public API patterns here:
  // /^\/api\/public\/.*$/,           // Example: public APIs
  // /^\/auth\/signin$/,              // Example: authentication endpoints
  // /^\/health$/,                    // Example: health check
]

// cd-0010: isUrlBlacklistedForSignedHeaders
// Returns true when the given URL/path is a public endpoint that should skip
// Signify waiting and signed-header attachment.
//
// Security notes:
//   - Pathname traversal (`..` segments) is rejected before pattern matching.
//     Without this guard, a crafted relative URL such as
//     `/v1/workflow/util/acceptance/../private/admin` could match the acceptance
//     prefix and cause the caller to skip the auth-signing path (because the
//     old unanchored `.match()` would find the acceptance substring in the
//     middle of the path).  The safe default on a traversal URL is to return
//     false (i.e. sign the request), because the caller treats blacklisted as
//     "skip auth".
//   - cd-0015: percent-encoded traversal (`%2e%2e`, `..%2f`, `%2e.`, `.%2e`
//     and uppercase variants) is NOT normalised by the WHATWG URL parser —
//     `new URL('https://x/%2e%2e/y').pathname` returns `/%2e%2e/y`, not `/../y`.
//     We therefore decode the path with `decodeURIComponent` before checking
//     for `..` segments.  If decoding throws (malformed %-sequence), we default
//     to `false` (sign the request) — the safer choice.  The decoded value is
//     used only for the traversal check; the original `path` is used for regex
//     matching to preserve existing pattern-match semantics.
//   - Pattern matching uses `.test()` (boolean short-circuit) rather than
//     `.match()` (array allocation + truthy coercion).
//   - All path patterns are anchored with ^ and $ (see BLACK_LIST_URL_PATTERNS).
//   - Call sites (apis.ts makeCustomFetch, services/signify.ts attachInterceptor)
//     extract `new URL(url).pathname` before calling this function.  The `..`
//     check here is a second line of defence for any caller that passes the raw
//     relative URL rather than a pre-parsed pathname.

// DF-2301: isValidPublicRecordLink
// Validates that a public-records evidence link is an adequate URL-like string.
//
// Rules (per DF-2301):
//   - Must be between 10 and 70 characters (inclusive).
//   - Must not contain characters outside basic Unicode (no emoji, no control chars).
//     Characters above U+FFFF (supplementary planes, including emoji) are rejected.
//     Control characters (U+0000–U+001F, U+007F–U+009F) are also rejected.
//   - Must have a domain: at least one dot with a TLD of ≥ 2 characters after it.
//   - If a scheme is present, it must be http:// or https:// (ftp://, etc. rejected).
//   - Scheme is optional: "google.com" is accepted (per ticket comment).
//
// The function does NOT attempt full RFC-3986 URL parsing; it applies only
// the lightweight checks described in the ticket so that the "Next" button
// on the Workflow/Prerequisites public-records step is gated on adequate input.
export function isValidPublicRecordLink(link: string): boolean {
  // Trim first — purely whitespace is rejected via the length check below.
  const trimmed = link.trim()

  // Length gate: 10 ≤ length ≤ 70
  if (trimmed.length < 10 || trimmed.length > 70) {
    return false
  }

  // Reject embedded whitespace (spaces, tabs, etc.) — a URL containing raw
  // whitespace is not URL-like even if it otherwise passes the length and TLD
  // checks (e.g. "https:// example.com").  Users who genuinely need a space
  // in a URL component must percent-encode it (%20).
  if (/\s/.test(trimmed)) {
    return false
  }

  // Reject characters outside basic Unicode (supplementary planes, emoji,
  // control characters, C1 controls).  We use a code-point scan so that a
  // surrogate pair (which represents a supplementary-plane character in JS
  // UCS-2 strings) is detected correctly.
  for (const char of trimmed) {
    const cp = char.codePointAt(0) ?? 0
    // Control characters (C0: 0x00–0x1F, DEL: 0x7F, C1: 0x80–0x9F)
    if (cp <= 0x1f || (cp >= 0x7f && cp <= 0x9f)) {
      return false
    }
    // Supplementary plane characters (includes all emoji): U+10000 and above
    if (cp >= 0x10000) {
      return false
    }
  }

  // Scheme check: if a scheme is present it must be http or https.
  const schemeMatch = trimmed.match(/^([a-z][a-z0-9+\-.]*):\/\//i)
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase()
    if (scheme !== 'http' && scheme !== 'https') {
      return false
    }
  }

  // Domain check: the string (or the part after the scheme) must contain a dot
  // followed by a TLD of at least 2 characters.  We strip the scheme prefix
  // before checking so that "https://" alone (no host) also fails.
  const afterScheme = schemeMatch
    ? trimmed.slice(schemeMatch[0].length)
    : trimmed

  // afterScheme must be non-empty and contain at least one dot where the
  // segment after the last dot is ≥ 2 characters (the TLD).
  if (!afterScheme) {
    return false
  }

  // Extract the host portion (everything before the first '/' or ':' after
  // the scheme, so that paths and ports don't confuse the TLD check).
  const hostPart = afterScheme.split(/[/:?#]/)[0]

  // hostPart must have a dot, and the segment after the final dot must be ≥ 2 chars.
  const lastDotIndex = hostPart.lastIndexOf('.')
  if (lastDotIndex === -1) {
    return false
  }
  const tld = hostPart.slice(lastDotIndex + 1)
  if (tld.length < 2) {
    return false
  }

  // Reject empty labels: a leading dot (`.com`) or consecutive dots
  // (`example..com`) indicate a malformed host that is not a valid domain.
  if (hostPart.startsWith('.') || hostPart.includes('..')) {
    return false
  }

  return true
}

export function isUrlBlacklistedForSignedHeaders(urlOrPath: string): boolean {
  // Extract pathname for the traversal check: if the input is a full URL, use
  // its pathname; otherwise treat the raw string as a path.  The regex match
  // (below) uses `urlOrPath` unchanged so that full-URL patterns (e.g. Jitsi
  // `^https://...`) continue to work exactly as before.
  let pathForTraversalCheck: string
  try {
    pathForTraversalCheck = new URL(urlOrPath).pathname
  } catch {
    pathForTraversalCheck = urlOrPath
  }

  // cd-0015: decode percent-encoded characters before checking for `..` segments.
  // This catches `%2e%2e`, `..%2f`, `%2e.`, `.%2e` and all case variants that
  // the WHATWG URL parser leaves un-normalised.  Malformed %-sequences cause
  // decodeURIComponent to throw — we treat that as "refuse to blacklist" (sign
  // the request), which is the safer default.
  let decoded: string
  try {
    decoded = decodeURIComponent(pathForTraversalCheck)
  } catch {
    return false
  }

  // cd-0010 + cd-0015: reject any path containing a literal `..` segment after
  // percent-decoding.  The safe default is to sign the request (return false).
  if (decoded.split('/').some((seg) => seg === '..')) {
    return false
  }

  return BLACK_LIST_URL_PATTERNS.some((pattern) => pattern.test(urlOrPath))
}
